import { useCallback, useEffect, useState, useRef } from "react";
import {
  Word,
  Position,
  Line,
  generateBackgroundLines,
  measureWordDimensions,
  calculateWordPositions,
} from "./utils";
import "./styles.scss";

export interface WordCloudProps {
  initialWords: Word[];
  words: Word[];
  onWordsChange: (words: Word[]) => void;
  maxSelectedWords?: number;
}

export const WordCloud = ({
  words,
  initialWords,
  onWordsChange,
  maxSelectedWords,
}: WordCloudProps) => {
  const [selectedWords, setSelectedWords] = useState<Word[]>(initialWords);
  const [wordPositions, setWordPositions] = useState<Position[]>([]);
  const [backgroundLines, setBackgroundLines] = useState<Line[]>([]);
  const [isPositioning, setIsPositioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const wordRefs = useRef<(HTMLDivElement | null)[]>([]);

  const positionWords = useCallback(async () => {
    if (!containerRef.current || isPositioning) return;

    setIsPositioning(true);

    // Wait for DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 100));

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    // Measure word dimensions
    const wordDimensions = measureWordDimensions(wordRefs, words);

    // Calculate positions
    const newPositions = calculateWordPositions(
      words,
      wordDimensions,
      containerRect
    );

    setWordPositions(newPositions);
    setIsPositioning(false);
  }, [words, isPositioning]);

  useEffect(() => {
    setBackgroundLines(generateBackgroundLines());
  }, []);

  useEffect(() => {
    positionWords();
  }, [words]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      setTimeout(positionWords, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [positionWords]);

  const handleWordClick = (word: Word) => {
    if (selectedWords.some(w => w.label === word.label)) {
      const newWords = selectedWords.filter(w => w.label !== word.label);
      setSelectedWords(newWords);
      onWordsChange?.(newWords.length ? newWords : []);
      return;
    }

    let newWords = [...selectedWords];
    if (maxSelectedWords && newWords.length >= maxSelectedWords) {
      newWords = newWords.filter((_, i) => i !== 0);
    }
    newWords = [...newWords, word];
    setSelectedWords(newWords);
    onWordsChange?.(newWords.length ? newWords : []);
  };

  return (
    <div className="tg_word-cloud">
      <svg
        className="background-lines"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {backgroundLines.map((line, index) => (
          <line
            key={index}
            x1={`${line.x1}%`}
            y1={`${line.y1}%`}
            x2={`${line.x2}%`}
            y2={`${line.y2}%`}
            stroke="var(--fl-color-primary)"
          />
        ))}
      </svg>

      <div className="words-container" ref={containerRef}>
        <div className="inner">
          {words.map((word, index) => {
            const isSelected = selectedWords.some(w => w.label === word.label);

            return (
              <div
                key={`${word}-${index}`}
                ref={el => (wordRefs.current[index] = el)}
                className={`cloud-word ${isSelected ? "selected" : ""}`}
                style={{
                  left: `${wordPositions[index]?.x || 0}%`,
                  top: `${wordPositions[index]?.y || 0}%`,
                }}
                onClick={() => {
                  handleWordClick(word);
                }}
              >
                {word.label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
